export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export interface ObjectDelta<T> {
    added?: DeepPartial<T>;
    updated?: DeepPartial<T>;
    deleted?: DeepPartial<T>;
}

/**
 *
 * @param original Original Object
 * @param updated Updated Object
 * @returns An object with up to 3 partial objects of the original, reflecting the changes
 * Note that this delta will not do a deep comparison for arrays of objects.  For an array of objects,
 * the resulting delta will either contain that array in the 'updated' property or not at all - it will not
 * determine added/deleted items
 */
export function getObjectDelta<T extends object>(original: T, updated: T): ObjectDelta<T> {
    const delta: ObjectDelta<T> = {};

    // Find added and updated properties
    for (const key in updated) {
        if (Object.prototype.hasOwnProperty.call(updated, key)) {
            const originalValue = original[key];
            const updatedValue = updated[key];

            if (!Object.prototype.hasOwnProperty.call(original, key)) {
                // Key added
                if (!delta.added) {
                    delta.added = {};
                }
                (delta.added as any)[key] = updatedValue;
            } else if (Array.isArray(updatedValue) && Array.isArray(originalValue)) {
                if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
                    if (
                        (updatedValue.length > 0 && typeof updatedValue[0] === 'object') ||
                        (originalValue.length > 0 && typeof originalValue[0] === 'object')
                    ) {
                        // For arrays of objects, the json comparison is as deep as we'll go, meaning we will not determine
                        // added/removed items - it's either updated or it's not
                        if (!delta.updated) {
                            delta.updated = {};
                        }
                        (delta.updated as any)[key] = updatedValue;
                    } else {
                        // For arrays of primitives, we can determine added/removed items
                        const addedItems = updatedValue.filter((item) => !originalValue.includes(item));
                        const removedItems = originalValue.filter((item) => !updatedValue.includes(item));
                        if (!delta.added) {
                            delta.added = {};
                        }
                        (delta.added as any)[key] = addedItems;
                        if (!delta.deleted) {
                            delta.deleted = {};
                        }
                        (delta.deleted as any)[key] = removedItems;
                    }
                }
            } else if (
                typeof updatedValue === 'object' &&
                updatedValue !== null &&
                typeof originalValue === 'object' &&
                originalValue !== null
            ) {
                // Deep comparison for nested objects/arrays
                const nestedDelta = getObjectDelta(originalValue as any, updatedValue as any);
                if (Object.keys(nestedDelta).length > 0) {
                    if (!delta.added) {
                        delta.added = {};
                    }
                    (delta.added as any)[key] = nestedDelta.added;
                    if (!delta.updated) {
                        delta.updated = {};
                    }
                    (delta.updated as any)[key] = nestedDelta.updated;
                    if (!delta.deleted) {
                        delta.deleted = {};
                    }
                    (delta.deleted as any)[key] = nestedDelta.deleted;
                }
            } else if (originalValue !== updatedValue) {
                // Primitive value updated
                if (!delta.updated) {
                    delta.updated = {};
                }
                (delta.updated as any)[key] = updatedValue;
            }
        }
    }

    // Find deleted properties
    for (const key in original) {
        if (Object.prototype.hasOwnProperty.call(original, key) && !Object.prototype.hasOwnProperty.call(updated, key)) {
            if (!delta.deleted) {
                delta.deleted = {};
            }
            (delta.deleted as any)[key] = original[key];
        }
    }

    return delta;
}
